import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  alloyAPI,
  customerAPI,
  dieMasterAPI,
  NalcoRateAPI,
  orderStatusAPI,
  salesOrderMaster,
  unitConversionAPI,
} from "../../utlis/apiService";
import Model from "../../common/Model";
import EntityAddModal from "../../common/EntityAddModal";
import { notify } from "../../common/Toast";
import InputField from "../../common/InputField";
import { MdDeleteForever } from "react-icons/md";
import { HiOutlinePlus } from "react-icons/hi";
import { FormHeader, FormFooter } from "../../common/Shared";
import Loader from "../../common/Loader";
import { SelectField } from "../../common/SelectField";

const MINIMUM_TARGET_KG_FOR_SUGGESTION = 350;

const generateItemId = () =>
  `item_${new Date().getTime()}_${Math.random().toString(36).substr(2, 9)}`;

const formatDateForDisplayHelper = (date) => {
  if (!date) return "N/A";
  try {
    const d = typeof date === "string" ? new Date(date) : date;
    if (isNaN(d.getTime())) return "Invalid Date";
    return d.toLocaleDateString("en-CA");
  } catch {
    return "Invalid Date";
  }
};

const SalesOrderForm = ({
  initialData,
  onSave,
  onCancel,
  isEditing,
  isSaving = false,
}) => {
  const today = new Date().toISOString()?.split("T")[0];
  const [orderDate, setOrderDate] = useState(today);
  const [availableDies, setAvailableDies] = useState([]);
  const [availableAlloys, setAvailableAlloys] = useState([]);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [committedDeliveryDays, setCommittedDeliveryDays] = useState("");
  const [calculatedDispatchDateDisplay, setCalculatedDispatchDateDisplay] =
    useState("N/A");
  const [errors, setErrors] = useState({});
  const [customerOptions, setCustomerOptions] = useState([]);
  const [modalType, setModalType] = useState(null);
  const lastUpdatedField = useRef(null);
  const [unitOptions, setUnitOptions] = useState([]);

  useEffect(() => {
    const fetchNalcoRate = async () => {
      try {
        const response = await NalcoRateAPI.getNalcoPrice();
        const rate = response?.data?.nalcoPrice || 0;
        setFormFields((prev) => ({
          ...prev,
          nalco_Rate: rate,
        }));
      } catch (err) {
        console.error("Error fetching Nalco Rate", err);
        notify("Failed to fetch Nalco rate", "error");
      }
    };

    if (!isEditing) {
      fetchNalcoRate();
    }
  }, [isEditing]);

  const handleNalcoPlusChange = (value) => {
    const plus = parseFloat(value) || 0;
    const base = parseFloat(formFields.nalco_Rate) || 0;
    const finalRate = (base + plus).toFixed(2);
    lastUpdatedField.current = "plus";
    setFormFields((prev) => ({
      ...prev,
      nalco_Addition: plus,
      final_Rate: finalRate,
    }));
  };

  const handleNalcoFinalRateChange = (value) => {
    const final = parseFloat(value) || 0;
    const base = parseFloat(formFields.nalco_Rate) || 0;
    const plus = (final - base).toFixed(2);
    lastUpdatedField.current = "final";
    setFormFields((prev) => ({
      ...prev,
      nalco_Addition: plus,
      final_Rate: final,
    }));
  };

  const createNewItem = () => ({
    id: generateItemId(),
    order_Detail_Id: 0,
    dieId: "",
    profileLengthFt: "",
    quantityPcs: "",
    qtyWeight: 0,
    coatingColor: "",
    unit_Conv_Id: "",
  });

  const [formFields, setFormFields] = useState({
    order_No: initialData?.order_No || 0,
    orderDate: initialData?.order_Date || today,
    customerId: initialData?.cust_Id || "",
    alloyId: initialData?.aolly_Id || "",
    statusId: initialData?.order_Status_Id || 8,
    total_Weight: initialData?.total_Weight || 0,
    total_Qty: initialData?.total_Qty || 0,
    deliveryDays: initialData?.delivery_Days?.toString() || "",
    calculate_dispatch_date: initialData?.calculate_dispatch_date || "",
    packingInstructions: initialData?.packing_Instructions || "",
    generalNotes: initialData?.general_Notes || "",
    nalco_Rate: initialData?.nalco_Rate ?? "",
    nalco_Addition: initialData?.nalco_Addition || 0,
    final_Rate: initialData?.final_Rate || 0,
  });

  const [items, setItems] = useState([createNewItem()]);
  const [lastEditedByItem, setLastEditedByItem] = useState({});
  const totalEstimatedWeight =
    items?.reduce((sum, i) => sum + Number(i.qtyWeight || 0), 0) || 0;

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [dies, alloys, statuses, customers, units] = await Promise.all([
          dieMasterAPI.getAll(),
          alloyAPI.getAll(),
          orderStatusAPI.getAll(),
          customerAPI.getAll(),
          unitConversionAPI.getAll(),
        ]);

        setCustomerOptions(
          customers.data.map((c) => ({
            value: c.customer_Id,
            label: c.name,
          }))
        );
        setAvailableDies(
          dies.data.map((die) => ({
            value: die.id,
            label: `${die.dieNumber}/${die.subDieNo} (${die.currentWeight})`,
            raw: die,
          }))
        );

        setAvailableAlloys(
          alloys.data.map((alloy) => ({
            value: alloy.id,
            label: alloy.name,
          }))
        );

        setAvailableStatuses(
          statuses.data.map((s) => ({
            value: s.order_Status_Id,
            label: s.name,
          }))
        );

        setUnitOptions(
          units.data.map((u) => ({
            value: u.unit_Conv_Id,
            label: u.unit_Conv_Name,
            raw: u.conv_Value,
          }))
        );
      } catch (err) {
        console.error("Error loading dropdowns", err);
      }
    };

    fetchDropdownData();
  }, []);

  useEffect(() => {
    const sourceItems = initialData?.items || initialData?.OrderDetails || [];
    if (sourceItems.length > 0) {
      const normalized = sourceItems.map((item) => ({
        id: generateItemId(),
        order_Detail_Id: item.order_Detail_Id || 0,
        dieId: item.dieId || item.Die_Id || "",
        profileLengthFt: item.profileLengthFt || item.Profile_Length || 0,
        unit_Conv_Id: item.unit_Conv_Id || item.unit_Conv_Id || "",
        quantityPcs: item.quantityPcs || item.Qty || 0,
        qtyWeight: item.qtyWeight || item.Qty_Weight || 0,
        coatingColor: item.coatingColor || item.Powder_Coating_Color || "",
        currentWeight: item.currentWeight || item.currentWeight,
      }));
      setItems(normalized);
    } else {
      setItems([createNewItem()]);
    }
  }, [initialData]);

  useEffect(() => {
    const oDate = new Date(orderDate);
    const daysNum = parseInt(committedDeliveryDays);
    if (
      orderDate &&
      !isNaN(oDate.getTime()) &&
      committedDeliveryDays.trim() !== "" &&
      !isNaN(daysNum) &&
      daysNum >= 0
    ) {
      const dispatchDate = new Date(oDate);
      dispatchDate.setDate(oDate.getDate() + daysNum);
      const formattedDate = formatDateForDisplayHelper(dispatchDate);
      setCalculatedDispatchDateDisplay(formattedDate);
      setFormFields((prev) => ({
        ...prev,
        calculate_dispatch_date: dispatchDate.toISOString()?.split("T")[0],
      }));
    } else {
      setCalculatedDispatchDateDisplay("N/A");
      setFormFields((prev) => ({
        ...prev,
        calculate_dispatch_date: "",
      }));
    }
  }, [orderDate, committedDeliveryDays]);

  useEffect(() => {
    if (initialData?.delivery_Days) {
      setCommittedDeliveryDays(initialData.delivery_Days.toString());
    }
  }, [initialData]);

  const getWeightPerPiece = (item) => {
    const dieRaw = availableDies.find((d) => d.value === item.dieId)?.raw;
    const weightPer12Ft = parseFloat(dieRaw?.currentWeightPer12Ft ?? 0);
    const dieWeight =
      weightPer12Ft > 0
        ? weightPer12Ft / 12
        : parseFloat(dieRaw?.currentWeight ?? 0);

    const unitConv =
      unitOptions.find((u) => u.value === item.unit_Conv_Id)?.raw ?? 1;
    const profileLen = parseFloat(item.profileLengthFt ?? 0);
    if (dieWeight <= 0 || profileLen <= 0 || unitConv <= 0) return 0;
    const perPiece = (dieWeight / unitConv) * profileLen;
    return perPiece > 0 ? perPiece : 0;
  };

  const recomputePair = (item, changedField, lastEdited) => {
    const perPiece = getWeightPerPiece(item);
    const pcs = parseFloat(item.quantityPcs || 0);
    const kg = parseFloat(item.qtyWeight || 0);

    const next = { ...item };
    if (changedField === "quantityPcs") {
      next.qtyWeight =
        perPiece > 0 ? parseFloat((pcs * perPiece).toFixed(2)) : 0;
      return next;
    }

    if (changedField === "qtyWeight") {
      next.quantityPcs =
        perPiece > 0 ? Math.max(1, Math.round(kg / perPiece)) : 0;
      return next;
    }
    if (["dieId", "unit_Conv_Id", "profileLengthFt"].includes(changedField)) {
      if (lastEdited === "qtyWeight") {
        next.quantityPcs =
          perPiece > 0 ? Math.max(1, Math.round(kg / perPiece)) : 0;
      } else {
        next.qtyWeight =
          perPiece > 0 ? parseFloat((pcs * perPiece).toFixed(2)) : 0;
      }
    }

    return next;
  };

  const updateItemField = (itemId, field, value) => {
    setItems((currentItems) =>
      currentItems.map((item) => {
        if (item.id !== itemId) return item;
        let updated = { ...item, [field]: value };

        if (field === "dieId") {
          const selectedDie = availableDies.find(
            (d) => d.value === updated.dieId
          )?.raw;
          updated.currentWeight = selectedDie?.currentWeight || 0;
        }
        const last = lastEditedByItem[itemId];
        updated = recomputePair(updated, field, last);
        updated = { ...updated, ...calculateItemSuggestions(updated) };

        return updated;
      })
    );
  };

  const addItem = () => setItems((prev) => [...prev, createNewItem()]);

  const removeItem = async (itemId) => {
    const itemToDelete = items.find((item) => item.id === itemId);

    if (!itemToDelete) return;

    const orderNo = formFields.order_No || 0;
    const orderDetailId = itemToDelete.order_Detail_Id || 0;

    if (orderNo > 0 && orderDetailId > 0) {
      try {
        await salesOrderMaster.deleteOrderDetails(orderDetailId, orderNo);
      } catch (error) {
        console.error("Failed to delete item from backend", error);
        alert("Error deleting item from server.");
        return;
      }
    }

    const updatedItems = items.filter((item) => item.id !== itemId);
    if (updatedItems.length === 0) {
      alert("An order must have at least one item.");
      return;
    }

    setItems(updatedItems);

    setErrors((prev) => {
      const newErrors = { ...prev };
      const index = items.findIndex((i) => i.id === itemId);
      if (index !== -1 && newErrors.items && newErrors.items.length > index) {
        newErrors.items.splice(index, 1);
        if (newErrors.items.every((e) => e === null)) {
          delete newErrors.items;
        }
      }
      return newErrors;
    });
  };

  const calculateItemSuggestions = useCallback(
    (item) => {
      const suggestions = {};
      const selectedDie = availableDies.find(
        (d) => d.value === item.dieId
      )?.raw;

      if (selectedDie && item.profileLengthFt > 0) {
        const weightPerFt =
          (selectedDie.currentWeightPer12Ft || selectedDie.currentWeight || 0) /
          12;
        const weightPerPiece = weightPerFt * item.profileLengthFt;

        if (weightPerPiece > 0) {
          const minPcs = Math.ceil(
            MINIMUM_TARGET_KG_FOR_SUGGESTION / weightPerPiece
          );
          suggestions.calculatedMinPcsFor350kg = minPcs;
          suggestions.minQtySuggestionText = `Min. Qty for ~${MINIMUM_TARGET_KG_FOR_SUGGESTION}kg: ${minPcs} pcs (approx. ${(
            minPcs * weightPerPiece
          ).toFixed(2)} kg)`;

          if (item.quantityPcs > 0) {
            const currentWeight = item.quantityPcs * weightPerPiece;
            suggestions.actualWeightForQuantityKg = currentWeight;
            suggestions.currentQtyWeightText = `Current Qty (${item.quantityPcs
              } pcs) Weight: ${currentWeight.toFixed(2)} kg`;
          } else {
            suggestions.currentQtyWeightText = `Current Qty (0 pcs) Weight: 0.00 kg`;
            suggestions.actualWeightForQuantityKg = 0;
          }
        } else {
          suggestions.minQtySuggestionText =
            "Cannot calculate suggestion (invalid die weight or profile length).";
          suggestions.currentQtyWeightText = "";
          suggestions.actualWeightForQuantityKg = 0;
        }
      } else {
        suggestions.minQtySuggestionText =
          "Select Die and enter Profile Length for suggestions.";
        suggestions.currentQtyWeightText = "";
        suggestions.actualWeightForQuantityKg = 0;
      }
      return suggestions;
    },
    [availableDies]
  );

  useEffect(() => {
    setItems((prevItems) =>
      prevItems?.map((item) => ({
        ...item,
        ...calculateItemSuggestions(item),
      }))
    );
  }, [
    items?.map((i) => i.id).join(),
    items?.map((i) => i.profileLengthFt).join(),
    items?.map((i) => i.quantityPcs).join(),
    calculateItemSuggestions,
  ]);

  const validateForm = () => {
    const newErrors = {};
    if (!formFields.order_No?.toString().trim())
      newErrors.orderNo = "Order Number is required.";
    // if (!customerName.trim())
    //   newErrors.customerName = "Customer Name is required.";
    if (!formFields.orderDate) newErrors.orderDate = "Order Date is required.";
    const daysNum = parseInt(committedDeliveryDays);
    if (committedDeliveryDays.trim() === "") {
      newErrors.committedDeliveryDays = "Committed Delivery Days is required.";
    } else if (isNaN(daysNum) || daysNum < 0 || !Number.isInteger(daysNum)) {
      newErrors.committedDeliveryDays = "Must be a non-negative whole number.";
    }
    // if (!status) newErrors.status = "Status is required.";

    const itemErrors = items?.map((item, index) => {
      const e = {};
      if (!item.dieId) e.dieId = "Die must be selected.";
      // if (!item.alloyId) e.alloyId = "Alloy is required.";
      const length = parseFloat(item.profileLengthFt);
      if (isNaN(length) || length <= 0)
        e.profileLengthFt = "Length must be positive.";
      const qty = parseInt(item.quantityPcs);
      if (isNaN(qty) || qty <= 0)
        e.quantityPcs = "Quantity must be a positive whole number.";
      return Object.keys(e).length > 0 ? e : null;
    });

    if (itemErrors?.some((e) => e !== null)) newErrors.items = itemErrors;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const isValid = validateForm();
    if (!isValid) {
      return;
    }
    const payload = {
      Order_No: formFields.order_No || 0,
      Order_Date: formFields.orderDate,
      Cust_Id: Number(formFields.customerId),
      Aolly_Id: Number(formFields.alloyId),
      Order_Status_Id: Number(formFields.statusId),
      Delivery_Days: Number(formFields.deliveryDays),
      Total_Weight: Number(formFields.total_Weight),
      Total_Qty: Number(formFields.total_Qty),
      Calculate_dispatch_date: formFields.calculate_dispatch_date,
      Packing_Instructions: formFields.packingInstructions,
      General_Notes: formFields.generalNotes,
      nalco_Rate: formFields.nalco_Rate,
      nalco_Addition: formFields.nalco_Addition,
      final_Rate: formFields.final_Rate,
      Created_By: 1,
      OrderDetails: items.map((item) => ({
        order_Detail_Id: item.order_Detail_Id || 0,
        Die_Id: Number(item.dieId),
        Profile_Length: item.profileLengthFt,
        Qty: item.quantityPcs,
        Qty_Weight: item.qtyWeight,
        Powder_Coating_Color: item.coatingColor,
        unit_Conv_Id: item.unit_Conv_Id,
        Created_By: 1,
      })),
    };

    onSave(payload, isEditing ? initialData?.order_No : 0);
  };

  const handleChange = (field, value) => {
    setFormFields((prev) => ({ ...prev, [field]: value }));
  };
  useEffect(() => {
    const totalQty = items.reduce(
      (sum, item) => sum + Number(item.quantityPcs || 0),
      0
    );
    const totalWeight = items.reduce(
      (sum, item) => sum + Number(item.qtyWeight || 0),
      0
    );

    setFormFields((prev) => ({
      ...prev,
      total_Qty: totalQty,
      total_Weight: totalWeight,
    }));
  }, [items]);

  const optionMap = {
    customer: {
      setter: setCustomerOptions,
      field: "customer_Id",
    },
    alloy: {
      setter: setAvailableAlloys,
      field: "id",
    },
  };
  const handleEntityAddSuccess = async (type) => {
    const { setter } = optionMap[type];

    try {
      let updatedData;

      switch (type) {
        case "customer":
          updatedData = await customerAPI.getAll();
          setter(
            updatedData.data.map((c) => ({
              value: c.customer_Id,
              label: c.name,
            }))
          );
          break;
        case "alloy":
          updatedData = await alloyAPI.getAll();
          setter(
            updatedData.data.map((s) => ({
              value: s.id,
              label: s.name,
            }))
          );
          break;
        default:
          return;
      }

      setModalType(null);
    } catch (err) {
      notify("Failed to refresh dropdown", "error");
    }
  };

  return (
    <div className="flex flex-col h-full bg-white overflow-hidden font-plus relative">
      {isSaving && <Loader centered />}
      <FormHeader
        title={isEditing ? "Update Sales Order" : "New Sales Order"}
        onCancel={onCancel}
      />

      <div className="flex-1 overflow-y-auto px-6 py-4 custom-scrollbar">
        <form onSubmit={handleSubmit} className="space-y-5">
          <section className="relative overflow-hidden group">
            {/* Section Header - Matching DieForm Style */}
            <div className="flex items-center gap-4 mb-4 border-b border-gray-100 pb-2">
              <span className="w-1.5 h-4 bg-primary"></span>
              <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[2.5px]">
                General Details
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <InputField
                label="Order Number"
                value={formFields.order_No}
                onChange={(v) => handleChange("order_No", v)}
                className="bg-slate-100 font-mono font-semibold"
                disabled
              />
              <InputField
                label="Customer"
                type="selectWithAdd"
                value={formFields.customerId}
                onChange={(selected) =>
                  handleChange("customerId", selected?.value)
                }
                placeholder="Select Customer"
                options={customerOptions}
                onAddNewClick={() => setModalType("customer")}
                required
              />
              <InputField
                label="Alloy"
                type="selectWithAdd"
                value={formFields.alloyId}
                onChange={(selected) =>
                  handleChange("alloyId", selected?.value)
                }
                placeholder="Select Alloy"
                options={availableAlloys}
                onAddNewClick={() => setModalType("alloy")}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 items-start">
              <InputField
                label="Order Date"
                type="date"
                value={formFields.orderDate}
                onChange={handleChange}
                error={errors.orderDate}
                required
              />
              <InputField
                label="Delivery Days"
                type="number"
                value={formFields.deliveryDays}
                onChange={(v) => {
                  handleChange("deliveryDays", v);
                  setCommittedDeliveryDays(v);
                }}
                unit="Days"
                placeholder="e.g., 7"
                required
              />
              <InputField label="Dispatch Date" value={calculatedDispatchDateDisplay} disabled />
            </div>
          </section>

          <section className="relative overflow-hidden group">
            <div className="flex items-center gap-4 mb-4 border-b border-gray-100 pb-2">
              <span className="w-1.5 h-4 bg-primary"></span>
              <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[2.5px]">
                Sales Order Detail
              </h3>
            </div>

            <div className="border border-gray-200 rounded-none overflow-hidden bg-white ">
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left border-collapse">
                  <thead className="bg-white text-gray-700 uppercase text-[10px] font-black tracking-[2.5px] border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4">Die No</th>
                      <th className="px-6 py-4">Weight</th>
                      <th className="px-6 py-4 text-center">Profile Length</th>
                      <th className="px-6 py-4 text-center">Qty</th>
                      <th className="px-6 py-4">Total Weight</th>
                      <th className="px-6 py-4">Coating</th>
                      <th className="px-6 py-4 text-center w-24">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {items.map((item, index) => (
                      <tr
                        key={item.id}
                        className="hover:bg-slate-50/80 transition-colors"
                      >
                        <td className="p-3 min-w-[180px]">
                          <InputField
                            type="select"
                            value={item.dieId}
                            onChange={(val) =>
                              updateItemField(item.id, "dieId", val)
                            }
                            options={availableDies}
                            defaultOptionLabel="Choose Die..."
                          />
                        </td>
                        <td className="p-3 min-w-[220px]">
                          <div className="flex items-center gap-1">
                            {/* Weight Field */}
                            <div className="flex-1">
                              <InputField
                                value={item.currentWeight || ""}
                                disabled
                                placeholder="Weight"
                                className="border-none bg-transparent font-medium"
                              />
                            </div>

                            {/* Unit Dropdown */}
                            <div className="w-[100px]">
                              <InputField
                                type="select"
                                value={item.unit_Conv_Id}
                                onChange={(val) =>
                                  updateItemField(
                                    item.id,
                                    "unit_Conv_Id",
                                    val,
                                  )
                                }
                                options={unitOptions}
                                className="border-none bg-transparent font-semibold text-primary"
                              />
                            </div>
                          </div>
                        </td>
                        <td className="p-3 w-28">
                          <InputField
                            type="number"
                            value={item.profileLengthFt}
                            onChange={(val) =>
                              updateItemField(
                                item.id,
                                "profileLengthFt",
                                parseFloat(val) || 0,
                              )
                            }
                          />
                        </td>
                        <td className="p-3 w-32">
                          <InputField
                            type="number"
                            value={item.quantityPcs}
                            onChange={(val) => {
                              setLastEditedByItem((m) => ({
                                ...m,
                                [item.id]: "quantityPcs",
                              }));
                              updateItemField(
                                item.id,
                                "quantityPcs",
                                parseInt(val) || 0,
                              );
                            }}
                            unit="pcs"
                          />
                        </td>
                        <td className="p-3">
                          <div className="font-semibold text-slate-700">
                            {item.qtyWeight.toFixed(2)} kg
                          </div>
                          <div className="text-[10px] text-sky-600 font-medium italic">
                            {item.minQtySuggestionText}
                          </div>
                        </td>
                        <td className="p-3">
                          <InputField
                            type="text"
                            value={item.coatingColor || ""}
                            onChange={(val) =>
                              updateItemField(item.id, "coatingColor", val)
                            }
                            placeholder="Color..."
                          />
                        </td>
                        <td className="p-3 text-center flex items-center justify-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => removeItem(item.id)}
                            className="p-1.5 border border-gray-200 text-red-600 hover:bg-red-50 transition-all rounded-none"
                            title="Remove Item"
                          >
                            <MdDeleteForever size={16} />
                          </button>

                          <button
                            type="button"
                            onClick={addItem}
                            className="p-1.5 border border-gray-200 text-blue-600 hover:bg-blue-50 transition-all rounded-none"
                            title="Add Item"
                          >
                            <HiOutlinePlus size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

              </div>
            </div>
          </section>

          <div className="mt-6 relative overflow-hidden group">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              {/* Left Column: Pricing & Inputs */}
              <div className="lg:col-span-3 space-y-4">
                <div className="flex items-center gap-4 mb-2 border-b border-gray-100 pb-2">
                  <span className="w-1.5 h-4 bg-primary"></span>
                  <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[2.5px]">
                    Pricing & Notes
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <InputField
                    label="Nalco Rate"
                    value={formFields.nalco_Rate}
                    disabled
                  />
                  <InputField
                    label="Nalco +"
                    type="number"
                    value={formFields.nalco_Addition}
                    onChange={handleNalcoPlusChange}
                    required
                  />
                  <InputField
                    label="Final Rate"
                    type="number"
                    value={formFields.final_Rate}
                    onChange={handleNalcoFinalRateChange}
                    required
                    className="bg-orange/5 font-bold"
                  />
                  <SelectField
                    label="Current Status"
                    value={formFields.statusId}
                    onChange={(v) => handleChange("statusId", v)}
                    options={availableStatuses}
                    required
                  />
                </div>

                <div className="space-y-4">
                  <InputField
                    type="textarea"
                    label="Packing Instructions"
                    value={formFields.packingInstructions}
                    onChange={(val) => handleChange("packingInstructions", val)}
                    rows={2}
                  />
                  <InputField
                    type="textarea"
                    label="General Notes"
                    value={formFields.generalNotes}
                    onChange={(val) => handleChange("generalNotes", val)}
                    rows={2}
                  />
                </div>
              </div>

              {/* Right Column: Standardized White Style Summary Card */}
              <div className="lg:col-span-2">
                <div className="relative overflow-hidden flex flex-col justify-center">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gray-50 -mr-16 -mt-16 rounded-full blur-3xl"></div>
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-primary/5 -ml-12 -mb-12 rounded-full blur-2xl"></div>

                  <div className="relative z-10 space-y-6">
                    <div className="flex items-center gap-4 mb-2 border-b border-gray-100 pb-2">
                      <span className="w-1.5 h-4 bg-primary"></span>
                      <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[2.5px]">
                        Order Summary
                      </h3>
                    </div>
                    <div className="space-y-6">
                      <div className="flex justify-between items-end border-b border-gray-50 pb-3 group/item hover:border-gray-100 transition-all">
                        <span className="text-[12px] font-bold text-gray-500 uppercase tracking-widest">Total Pcs</span>
                        <div className="flex items-baseline gap-2">
                          <span className="text-[20px] font-black text-slate-900 leading-none">
                            {items?.reduce((sum, item) => sum + (item.quantityPcs || 0), 0).toLocaleString()}
                          </span>
                          <span className="text-[10px] font-bold text-gray-400 uppercase">pcs</span>
                        </div>
                      </div>

                      <div className="flex justify-between items-end group/item hover:border-gray-100 transition-all">
                        <span className="text-[12px] font-bold text-gray-500 uppercase tracking-widest"> Total Estimated Weight</span>
                        <div className="flex items-baseline gap-2">
                          <span className="text-[20px] font-black text-primary tracking-tight leading-none">
                            {totalEstimatedWeight.toFixed(2)}
                          </span>
                          <span className="text-[10px] font-bold text-gray-400 uppercase">kg</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </form>
      </div>

      <FormFooter
        onCancel={onCancel}
        onSubmit={handleSubmit}
        isSaving={isSaving}
        submitText={isEditing ? "Update" : "Save"}
      />

      <Model
        isOpen={!!modalType}
        onClose={() => setModalType(false)}
        name={`Add ${modalType}`}
      >
        <EntityAddModal
          type={modalType}
          onClose={(newOption) => {
            setModalType(null);
            handleEntityAddSuccess(modalType, newOption);
          }}
        />
      </Model>
    </div>
  );
};

export default SalesOrderForm;
